import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
    FaArrowLeft,
    FaArrowRight,
    FaChalkboardTeacher,
    FaEnvelope,
    FaEye,
    FaEyeSlash,
    FaGoogle,
    FaLock,
    FaRocket,
    FaSchool,
    FaUserGraduate
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import {
    getDefaultPage,
    hasCompletedOnboarding,
    isGuestUser,
    requiresApproval
} from '../../utils/rbac';
import { in3dFontStyle, TrademarkSymbol } from '../In3DTypography';
import LandingScene from '../LandingScene';

const CYAN = '#5FE0FB';
const VIOLET = '#8B5CF6';

const FIELD =
  'w-full rounded-xl border border-white/12 bg-white/[0.04] py-3.5 text-[15px] text-white placeholder:text-white/30 transition-colors focus:border-[#5FE0FB]/60 focus:outline-none focus:ring-2 focus:ring-[#5FE0FB]/25 disabled:opacity-50';

const ROLE_TILE =
  'group relative flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-left transition-colors hover:border-white/30 hover:bg-white/[0.07]';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('role-select');
  const { login, loginWithGoogle, loginAsGuestStudent, user, profile, selectedRole, setSelectedRole } = useAuth();
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  // LandingScene reads this each frame; the login screen holds it steady.
  const sceneProgress = useRef(0.18);

  const roleOptions = [
    {
      id: 'student',
      icon: FaUserGraduate,
      title: 'Student',
      description: 'Access lessons and complete interactive quizzes',
      tint: CYAN,
    },
    {
      id: 'teacher',
      icon: FaChalkboardTeacher,
      title: 'Teacher',
      description: 'Create and manage educational content',
      tint: VIOLET,
    },
    {
      id: 'school',
      icon: FaSchool,
      title: 'School Administrator',
      description: 'Manage school-wide content and teachers',
      tint: '#34D399',
    },
  ];

  useEffect(() => {
    const handleRedirect = async () => {
      if (!user || !profile) return;
      if (!hasCompletedOnboarding(profile)) {
        navigate('/onboarding');
        return;
      }
      if (!isGuestUser(profile) && requiresApproval(profile.role) && profile.approvalStatus === 'pending') {
        navigate('/approval-pending');
        return;
      }
      navigate(getDefaultPage(profile.role));
    };
    handleRedirect();
  }, [user, profile, navigate]);

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    setStep('login-form');
  };

  const handleBackToRoleSelect = () => {
    setStep('role-select');
    setError('');
  };

  const handleGuestLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await loginAsGuestStudent();
    } catch (err) {
      setError(err?.message || 'Could not start guest session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      setError('');
      await login(email, password);
    } catch (err) {
      setError('Failed to login. ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      setError('');
      await loginWithGoogle(selectedRole);
    } catch (err) {
      setError('Failed to login with Google. ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleDisplayName = () => {
    const role = roleOptions.find(r => r.id === selectedRole);
    return role?.title || 'User';
  };

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  // Login page is dark mode only
  useEffect(() => {
    setTheme('dark');
  }, [setTheme]);

  const panel = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25, 0.4, 0.25, 1] } },
    exit: { opacity: 0, y: -14, transition: { duration: 0.2 } },
  };

  return (
    <div className="grid h-[100dvh] max-h-[100dvh] w-screen grid-cols-1 overflow-hidden bg-[#04070f] font-body text-white lg:grid-cols-[1.05fr_minmax(480px,0.95fr)]">
      {/* ---------- Left: the scene ---------- */}
      <aside className="relative hidden overflow-hidden lg:block">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(760px 620px at 40% 32%, rgba(139,92,246,0.26), transparent 70%), radial-gradient(620px 520px at 78% 82%, rgba(95,224,251,0.18), transparent 72%)',
          }}
        />
        <div className="pointer-events-none absolute inset-0">
          <LandingScene scroll={sceneProgress} />
        </div>
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(4,7,15,0.35) 0%, transparent 35%, rgba(4,7,15,0.9) 100%)' }}
        />

        <div className="relative z-10 flex h-full flex-col justify-between p-12 xl:p-16">
          <Link to="/" className="inline-flex items-center gap-2.5 self-start text-white">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={CYAN} strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2.5 21 7.5v9L12 21.5 3 16.5v-9z" />
              <path d="M3 7.5l9 5 9-5" />
              <path d="M12 12.5v9" />
            </svg>
            <span style={in3dFontStyle} className="font-display text-[19px] font-bold tracking-[0.08rem]">
              In3D<span style={{ color: CYAN }}>.ai</span>
              <TrademarkSymbol className="ml-1" />
            </span>
          </Link>

          <div className="max-w-[440px]">
            <h1 className="font-display text-[46px] font-extrabold leading-[1.0] tracking-[-0.035em] xl:text-[56px]">
              Step inside
              <span
                className="block bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(100deg, ${CYAN} 0%, #C4B5FD 52%, ${VIOLET} 100%)` }}
              >
                the lesson
              </span>
            </h1>
            <p className="mt-5 text-[16px] leading-[1.7] text-white/60">
              Immersive, curriculum-aligned lessons. 360° environments, interactive 3D models, and a syllabus
              teachers keep under their own control.
            </p>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/35">
            CBSE aligned · English &amp; Hindi
          </p>
        </div>
      </aside>

      {/* ---------- Right: the form ---------- */}
      <main className="relative flex min-h-0 flex-col overflow-y-auto bg-[#070b16] lg:border-l lg:border-white/10">
        <div
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background:
              'radial-gradient(600px 420px at 20% 10%, rgba(139,92,246,0.2), transparent 68%), radial-gradient(520px 400px at 85% 88%, rgba(95,224,251,0.14), transparent 70%)',
          }}
        />

        <div className="relative z-10 flex min-h-full flex-col justify-center px-6 py-10 sm:px-10 lg:px-14">
          <Link to="/" className="mb-10 inline-flex items-center gap-2.5 self-start text-white lg:hidden">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={CYAN} strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2.5 21 7.5v9L12 21.5 3 16.5v-9z" />
              <path d="M3 7.5l9 5 9-5" />
              <path d="M12 12.5v9" />
            </svg>
            <span style={in3dFontStyle} className="font-display text-[18px] font-bold tracking-[0.08rem]">
              In3D<span style={{ color: CYAN }}>.ai</span>
            </span>
          </Link>

          <div className="mx-auto w-full max-w-[420px]">
            <AnimatePresence mode="wait">
              {step === 'role-select' ? (
                <motion.div key="role-select" variants={panel} initial="hidden" animate="visible" exit="exit">
                  <h2 className="font-display text-[34px] font-bold leading-[1.1] tracking-[-0.03em]">Sign in</h2>
                  <p className="mt-2.5 text-[15px] text-white/55">Choose how you use In3D.ai.</p>

                  <div className="mt-9 flex flex-col gap-3">
                    {roleOptions.map((role) => (
                      <button key={role.id} type="button" onClick={() => handleRoleSelect(role.id)} className={ROLE_TILE}>
                        <span
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10"
                          style={{ backgroundColor: `${role.tint}1f` }}
                        >
                          <role.icon className="text-[16px]" style={{ color: role.tint }} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-display text-[16px] font-semibold text-white">{role.title}</span>
                          <span className="mt-0.5 block text-[13px] leading-snug text-white/50">{role.description}</span>
                        </span>
                        <FaArrowRight
                          className="h-3.5 w-3.5 shrink-0 text-white/30 transition-transform group-hover:translate-x-1 group-hover:text-white/70"
                          aria-hidden
                        />
                      </button>
                    ))}
                  </div>

                  <div className="my-7 flex items-center gap-3">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">or</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>

                  <button
                    type="button"
                    onClick={handleGuestLogin}
                    disabled={isLoading}
                    className="group flex w-full items-center gap-4 rounded-2xl border border-dashed px-5 py-4 text-left transition-colors disabled:opacity-60"
                    style={{ borderColor: 'rgba(95,224,251,0.35)', backgroundColor: 'rgba(95,224,251,0.06)' }}
                  >
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                      style={{ backgroundImage: `linear-gradient(135deg, ${CYAN}, ${VIOLET})` }}
                    >
                      <FaRocket className="text-[16px] text-[#04121b]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-[16px] font-semibold text-white">Explore as guest</span>
                      <span className="mt-0.5 block text-[13px] leading-snug text-white/50">
                        Try one lesson — no account needed
                      </span>
                    </span>
                    {isLoading ? (
                      <span className="shrink-0 animate-pulse text-[13px] text-white/50">Starting…</span>
                    ) : (
                      <FaArrowRight className="h-3.5 w-3.5 shrink-0 text-white/30" aria-hidden />
                    )}
                  </button>

                  <p className="mt-9 text-[14px] text-white/50">
                    Don&apos;t have an account?{' '}
                    <Link to="/signup" className="font-medium underline-offset-2 hover:underline" style={{ color: CYAN }}>
                      Create account
                    </Link>
                  </p>
                  <p className="mt-2 text-[13px] text-white/40">
                    Want to see more?{' '}
                    <Link to="/web-preview" className="underline-offset-2 hover:underline" style={{ color: CYAN }}>
                      Platform tour
                    </Link>
                  </p>
                </motion.div>
              ) : (
                <motion.div key="login-form" variants={panel} initial="hidden" animate="visible" exit="exit">
                  <button
                    type="button"
                    onClick={handleBackToRoleSelect}
                    className="-ml-1 mb-7 inline-flex items-center gap-2 text-[13px] text-white/50 transition-colors hover:text-white"
                  >
                    <FaArrowLeft className="h-3 w-3" />
                    Change role
                  </button>

                  <h2 className="font-display text-[34px] font-bold leading-[1.1] tracking-[-0.03em]">Welcome back</h2>
                  <p className="mt-2.5 text-[15px] text-white/55">
                    Signing in as{' '}
                    <span className="font-medium" style={{ color: CYAN }}>{getRoleDisplayName()}</span>
                  </p>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      role="alert"
                      className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-[13px] text-red-200"
                    >
                      {error}
                    </motion.div>
                  )}

                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={isLoading}
                    className="mt-8 flex w-full items-center justify-center gap-3 rounded-xl border border-white/12 bg-white/[0.04] py-3.5 text-[15px] font-medium text-white transition-colors hover:border-white/30 hover:bg-white/[0.07] disabled:opacity-50"
                  >
                    <FaGoogle className="h-4 w-4" />
                    Continue with Google
                  </button>

                  <div className="my-6 flex items-center gap-3">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">or with email</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="login-email" className="mb-2 block text-[12px] font-medium uppercase tracking-[0.1em] text-white/50">
                        Email
                      </label>
                      <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
                        <input
                          id="login-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          required
                          disabled={isLoading}
                          autoComplete="email"
                          className={`${FIELD} pl-11 pr-4`}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="login-password" className="mb-2 block text-[12px] font-medium uppercase tracking-[0.1em] text-white/50">
                        Password
                      </label>
                      <div className="relative">
                        <FaLock className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
                        <input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          disabled={isLoading}
                          autoComplete="current-password"
                          className={`${FIELD} pl-11 pr-12`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-white/40 transition-colors hover:text-white"
                        >
                          {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Link to="/forgot-password" className="text-[13px] text-white/45 transition-colors hover:text-white">
                        Forgot password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-[15px] font-semibold text-[#04121b] transition-opacity hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundImage: `linear-gradient(100deg, ${CYAN}, #A78BFA)` }}
                    >
                      {isLoading ? (
                        <span className="animate-pulse">Signing in…</span>
                      ) : (
                        <>
                          Sign In
                          <FaArrowRight className="h-3.5 w-3.5" />
                        </>
                      )}
                    </button>
                  </form>

                  <p className="mt-8 text-[14px] text-white/50">
                    Don&apos;t have an account?{' '}
                    <Link to="/signup" className="font-medium underline-offset-2 hover:underline" style={{ color: CYAN }}>
                      Create account
                    </Link>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
};
